import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "convex/react";
import { History, RotateCcw, Save, Send } from "lucide-react";
import { Link } from "react-router";
import {
  statutScorecard,
  validerScorecard,
  type ContenuScorecardSaisi,
} from "@repo/ent-struct";
import { Button, Loader, PageHeader } from "@repo/ui";
import { cn } from "@repo/utils";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { BandeauValidation } from "./BandeauValidation";
import type { LigneScorecard } from "./ListeScorecards";
import { indexerProblemes, messageErreur } from "./edition";
import { ProblemesContext } from "./formulaire/commun";
import { FormulaireScorecard } from "./formulaire/FormulaireScorecard";
import { OngletJson } from "./OngletJson";
import { BandeauStatut, DialoguePublier, resumeStatut } from "./Publication";

type Onglet = "formulaire" | "json";

/** Modifications en cours, et la version dont elles partent. */
type Brouillon = { base: number; contenu: ContenuScorecardSaisi };

/**
 * Éditeur d'une scorecard (portail client et portail admin).
 *
 * Deux gestes distincts :
 * - **Enregistrer** crée une version de travail, même incomplète. Les
 *   modifications locales n'existent qu'une fois la première faite ; après
 *   l'enregistrement elles sont jetées, et l'écran repart de la nouvelle
 *   version — qui porte les IDs attribués par le serveur aux ajouts.
 * - **Publier** met la version courante en service pour les nouvelles
 *   campagnes. Exige un contenu valide et aucune modification en attente : on
 *   publie ce que le serveur a, pas ce qui est à l'écran.
 */
export function ScorecardEditor({
  scorecardId,
  lienHistorique,
  avisBrouillon,
  actions,
}: {
  scorecardId: Id<"scorecards">;
  lienHistorique: string;
  /** Avis affiché tant que la scorecard est un brouillon jamais publié. */
  avisBrouillon?: ReactNode;
  /** Actions propres au portail, placées avant celles de l'éditeur. */
  actions?: (scorecard: LigneScorecard) => ReactNode;
}) {
  const donnees = useQuery(api.scorecards.obtenirScorecard, { scorecardId });
  const enregistrer = useMutation(api.scorecards.enregistrerScorecard);
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [onglet, setOnglet] = useState<Onglet>("formulaire");
  const [enCours, setEnCours] = useState(false);
  const [erreurServeur, setErreurServeur] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [publication, setPublication] = useState(false);
  // Change à chaque abandon du brouillon : l'onglet JSON, qui garde son texte
  // en local, repart alors du contenu affiché.
  const [generation, setGeneration] = useState(0);

  // Quitter la page avec un brouillon : le navigateur demande confirmation.
  useEffect(() => {
    if (!brouillon) return;
    const avertir = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", avertir);
    return () => window.removeEventListener("beforeunload", avertir);
  }, [brouillon]);

  if (donnees === undefined) {
    return (
      <div className="flex justify-center p-16">
        <Loader />
      </div>
    );
  }

  const { scorecard, version } = donnees;
  // Titre de l'en-tête : un renommage ne crée pas de version, et la prochaine
  // version doit porter ce nom.
  const contenu: ContenuScorecardSaisi = brouillon?.contenu ?? {
    titre: scorecard.titre,
    description: version.description,
    competences: version.competences,
  };
  const rapport = validerScorecard(contenu);
  const archivee = scorecard.archivedAt !== undefined;
  const statut = statutScorecard(
    scorecard.numeroVersionCourante,
    scorecard.numeroVersionPubliee,
  );
  const peutPublier =
    !brouillon && !archivee && statut !== "publiee" && rapport.erreurs.length === 0;
  // Quelqu'un a enregistré depuis le début de ces modifications : le serveur
  // refusera, autant le dire avant.
  const depassee =
    brouillon !== null && brouillon.base !== scorecard.numeroVersionCourante;

  const modifier = (nouveau: ContenuScorecardSaisi) => {
    setConfirmation(null);
    setErreurServeur(null);
    setBrouillon({
      base: brouillon?.base ?? scorecard.numeroVersionCourante,
      contenu: nouveau,
    });
  };

  const abandonner = () => {
    setBrouillon(null);
    setGeneration((g) => g + 1);
  };

  const sauvegarder = async () => {
    if (!brouillon) return;
    setEnCours(true);
    setErreurServeur(null);
    try {
      const { numero } = await enregistrer({
        scorecardId,
        numeroVersionAttendu: brouillon.base,
        ...brouillon.contenu,
      });
      abandonner();
      setConfirmation(`Version ${numero} enregistrée — pas encore publiée.`);
    } catch (e) {
      setErreurServeur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    // `contain: inline-size` : la largeur vient du parent, jamais du contenu.
    // Le `body` du portail client est une grille, qui s'élargirait sinon à la
    // longueur d'un texte de question tronqué (`nowrap`).
    <div className="space-y-6 [contain:inline-size]">
      <PageHeader
        titre={scorecard.titre}
        // Texte seul : le sous-titre est un <p>, un Badge (div) n'y a pas sa place.
        sousTitre={`${resumeStatut(scorecard.numeroVersionCourante, scorecard.numeroVersionPubliee)}${archivee ? " · archivée" : ""}`}
        actions={
          <>
            {actions?.(scorecard)}
            <Button variant="outline" asChild>
              <Link to={lienHistorique}>
                <History /> Historique
              </Link>
            </Button>
            {brouillon && (
              <Button variant="ghost" onClick={abandonner} disabled={enCours}>
                <RotateCcw /> Annuler les modifications
              </Button>
            )}
            {/* Les erreurs ne bloquent plus l'enregistrement : seulement la publication. */}
            <Button
              variant={brouillon ? "default" : "outline"}
              onClick={() => void sauvegarder()}
              disabled={!brouillon || enCours || archivee}
            >
              <Save /> {enCours ? "Enregistrement…" : "Enregistrer"}
            </Button>
            <Button
              variant={peutPublier ? "default" : "outline"}
              onClick={() => setPublication(true)}
              disabled={!peutPublier}
              title={
                brouillon
                  ? "Enregistrez d'abord vos modifications"
                  : rapport.erreurs.length > 0
                    ? "Corrigez les erreurs avant de publier"
                    : statut === "publiee"
                      ? "Cette version est déjà publiée"
                      : undefined
              }
            >
              <Send /> Publier
            </Button>
          </>
        }
        className="mb-0"
      />

      <BandeauStatut
        numeroCourant={scorecard.numeroVersionCourante}
        numeroPublie={scorecard.numeroVersionPubliee}
        modele={scorecard.organisationId === undefined}
      />
      {statut === "brouillon" && avisBrouillon}
      {archivee && (
        <p className="rounded-surface border border-border bg-muted p-3 text-sm">
          Cette scorecard est archivée : désarchivez-la depuis la liste pour la
          modifier.
        </p>
      )}
      {depassee && (
        <p role="alert" className="rounded-surface border border-etat-erreur-trait bg-etat-erreur-surface p-3 text-sm">
          La scorecard a été modifiée ailleurs (version {scorecard.numeroVersionCourante})
          depuis le début de vos modifications. Annulez-les pour repartir de la
          dernière version.
        </p>
      )}
      {erreurServeur && (
        <p role="alert" className="rounded-surface border border-etat-erreur-trait bg-etat-erreur-surface p-3 text-sm">
          {erreurServeur}
        </p>
      )}
      {confirmation && (
        <p role="status" className="rounded-surface border border-etat-fait-trait bg-etat-fait-surface p-3 text-sm text-etat-fait-contenu">
          {confirmation}
        </p>
      )}
      <BandeauValidation rapport={rapport} />

      <div role="tablist" aria-label="Mode d'édition" className="flex gap-1 border-b border-border">
        {(
          [
            ["formulaire", "Formulaire"],
            ["json", "JSON"],
          ] as const
        ).map(([cle, libelle]) => (
          <button
            key={cle}
            type="button"
            role="tab"
            aria-selected={onglet === cle}
            onClick={() => setOnglet(cle)}
            className={cn(
              "-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors hover:bg-charte-hover",
              onglet === cle
                ? "border-charte-accent text-foreground"
                : "border-transparent text-muted-foreground",
            )}
          >
            {libelle}
          </button>
        ))}
      </div>

      <ProblemesContext.Provider value={indexerProblemes(rapport.erreurs)}>
        {onglet === "formulaire" ? (
          <FormulaireScorecard contenu={contenu} onChange={modifier} />
        ) : (
          <OngletJson key={generation} contenu={contenu} onChange={modifier} />
        )}
      </ProblemesContext.Provider>

      {publication && (
        <DialoguePublier
          scorecardId={scorecardId}
          numero={scorecard.numeroVersionCourante}
          onFermer={() => setPublication(false)}
          onPubliee={(numero) => {
            setPublication(false);
            setConfirmation(`Version ${numero} publiée : les nouvelles campagnes l'utiliseront.`);
          }}
        />
      )}
    </div>
  );
}
